import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import type { PoseLandmarkerResult } from '@mediapipe/tasks-vision';

/**
 * MediaPipe Pose loader (docs/ARCHITECTURE.md §1.1).
 *
 * Everything is self-hosted: `public/wasm` and `public/models` are populated by
 * `scripts/fetch-models.ts` and verified against `models.lock.json`. There is no
 * runtime CDN dependency — a blocked CDN would take posture analysis offline and
 * would also leak that a user is running a session.
 */

export type PoseModel = 'lite' | 'full';
export type Delegate = 'GPU' | 'CPU';

export class PoseModelLoadError extends Error {
  constructor(
    message: string,
    readonly code: 'model_load' | 'gpu_fail',
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'PoseModelLoadError';
  }
}

export interface LoadedLandmarker {
  landmarker: PoseLandmarker;
  model: PoseModel;
  delegate: Delegate;
  close(): void;
}

const MODEL_PATH: Record<PoseModel, string> = {
  lite: '/models/pose_landmarker_lite.task',
  full: '/models/pose_landmarker_full.task',
};

let current: LoadedLandmarker | null = null;
let inflight: Promise<LoadedLandmarker> | null = null;

async function create(model: PoseModel, delegate: Delegate): Promise<PoseLandmarker> {
  const vision = await FilesetResolver.forVisionTasks('/wasm');
  return PoseLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: MODEL_PATH[model],
      delegate,
    },
    runningMode: 'VIDEO',
    // 2 poses so a second person walking through is *detected* and pauses the
    // session, rather than silently corrupting the measurement.
    numPoses: 2,
    minPoseDetectionConfidence: 0.5,
    minPosePresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
    outputSegmentationMasks: false,
  });
}

/**
 * Loads (and caches) the landmarker. Falls back GPU → CPU once; the caller is
 * told which delegate won so the session can drop to 2 fps on CPU.
 */
export async function loadLandmarker(model: PoseModel): Promise<LoadedLandmarker> {
  if (current && current.model === model) return current;
  if (inflight) return inflight;

  inflight = (async () => {
    current?.close();
    current = null;
    let landmarker: PoseLandmarker;
    let delegate: Delegate = 'GPU';
    try {
      landmarker = await create(model, 'GPU');
    } catch (gpuError) {
      try {
        landmarker = await create(model, 'CPU');
        delegate = 'CPU';
      } catch (cpuError) {
        throw new PoseModelLoadError(
          'Could not load the posture model. Check your connection and reload.',
          'model_load',
          cpuError ?? gpuError,
        );
      }
    }
    const loaded: LoadedLandmarker = {
      landmarker,
      model,
      delegate,
      close() {
        landmarker.close();
        if (current === loaded) current = null;
      },
    };
    current = loaded;
    return loaded;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function detectOnVideo(
  loaded: LoadedLandmarker,
  video: HTMLVideoElement,
  timestampMs: number,
): PoseLandmarkerResult {
  return loaded.landmarker.detectForVideo(video, timestampMs);
}

export function releaseLandmarker(): void {
  current?.close();
  current = null;
}

/**
 * Mean luminance of the current frame, used only by the quality gate.
 *
 * This is the one place pixels are read. They are downsampled to 32×24 inside an
 * OffscreenCanvas, reduced to a single number, and thrown away. No pixel buffer
 * is stored, uploaded, or converted to a data URL anywhere in this codebase
 * (CLAUDE.md "하지 말 것").
 */
const lumaCanvas: { canvas: OffscreenCanvas; ctx: OffscreenCanvasRenderingContext2D } | null =
  typeof OffscreenCanvas === 'undefined'
    ? null
    : (() => {
        const canvas = new OffscreenCanvas(32, 24);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        return ctx ? { canvas, ctx } : null;
      })();

export function meanLuma(video: HTMLVideoElement): number | null {
  if (!lumaCanvas || video.readyState < 2) return null;
  const { canvas, ctx } = lumaCanvas;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let sum = 0;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    // Rec. 601 luma.
    sum += 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
  }
  return sum / pixels;
}
